import type { UnitStatReportViewModel } from 'src/api/types/UnitStatReportViewModel';

import dayjs from 'dayjs';
import { useState, useEffect, type Dispatch, type SetStateAction } from 'react';

import { DatePicker } from '@mui/x-date-pickers';
import { Box, Grid, MenuItem, Skeleton, TextField, Typography } from '@mui/material';

import {
  mapPlanTypeCodeToDescription,
  mapEstimatedAuditCodeToDescription,
  mapCorrectionTypeCodeToDescription,
} from 'src/sections/employer-data/shared/utils/policyUtils';

import type {
  UnitStatHeaderDraft,
  UnitStatHeaderLookups,
  UnitStatHeaderFieldErrors,
} from './unit-stat-header-edit.helpers';

type UnitStatHeaderEditProps = {
  data?: UnitStatReportViewModel;
  draft: UnitStatHeaderDraft;
  setDraft: Dispatch<SetStateAction<UnitStatHeaderDraft>>;
  lookups: UnitStatHeaderLookups;
  errors: UnitStatHeaderFieldErrors;
  loading?: boolean;
};

const POLICY_INFO_FIELD_COUNT = 10;
const POLICY_TYPE_FIELD_COUNT = 8;

// Matches the codes/labels used by mapPlanTypeCodeToDescription in view mode; 03-06 map to 'Invalid' and are excluded.
const MARKET_TYPE_OPTIONS = ['01', '02', '07'].map((code) => ({
  code,
  description: mapPlanTypeCodeToDescription(code),
}));

const AUDIT_COMPLETE_OPTIONS = ['Y', 'N', 'U'].map((code) => ({
  code,
  description: mapEstimatedAuditCodeToDescription(code),
}));

const ALLOWED_COVERAGE_TYPE_CODES = ['01', '09'];
const ALLOWED_CORRECTION_TYPE_CODES = ['E', 'H', 'M', 'T', 'L'];
const EXCLUDED_BASIS_DEDUCTIBLE_CALCULATION_CODES = ['08', '07', '11', '04', '05', '13'];

const mapCorrectionTypeOptionDescription = (option: { code: string; description: string }) => ({
  ...option,
  description: mapCorrectionTypeCodeToDescription(option.code) || option.description,
});

export const getAllowedCoverageTypeOptions = (
  options: Array<{ code: string; description: string }>,
  currentValue: string | number | null | undefined
) => {
  const filtered: Array<{ code: string; description: string; disabled?: boolean }> = options.filter(
    (option) => ALLOWED_COVERAGE_TYPE_CODES.includes(option.code)
  );
  const currentCode = currentValue?.toString();

  if (currentCode && !ALLOWED_COVERAGE_TYPE_CODES.includes(currentCode)) {
    const currentDescription =
      options.find((option) => option.code === currentCode)?.description ?? currentCode;
    filtered.push({ code: currentCode, description: currentDescription, disabled: true });
  }

  return filtered;
};

export const getAllowedCorrectionTypeOptions = (
  options: Array<{ code: string; description: string }>,
  currentValue: string | number | null | undefined
) => {
  const filtered: Array<{ code: string; description: string; disabled?: boolean }> = options
    .filter((option) => ALLOWED_CORRECTION_TYPE_CODES.includes(option.code))
    .map(mapCorrectionTypeOptionDescription);
  filtered.unshift({ code: '', description: '' });
  const currentCode = currentValue?.toString();

  if (currentCode && !ALLOWED_CORRECTION_TYPE_CODES.includes(currentCode)) {
    const currentOption = options.find((option) => option.code === currentCode);
    const currentDescription = currentOption
      ? mapCorrectionTypeOptionDescription(currentOption).description
      : currentCode;
    filtered.push({ code: currentCode, description: currentDescription, disabled: true });
  }

  return filtered;
};

export const getAllowedBasisDeductibleCalculationOptions = (
  options: Array<{ code: string; description: string }>,
  currentValue: string | number | null | undefined
) => {
  const currentCode = currentValue?.toString();
  const filtered: Array<{ code: string; description: string; disabled?: boolean }> = options.filter(
    (option) => !EXCLUDED_BASIS_DEDUCTIBLE_CALCULATION_CODES.includes(option.code)
  );

  if (currentCode && EXCLUDED_BASIS_DEDUCTIBLE_CALCULATION_CODES.includes(currentCode)) {
    const currentOption = options.find((option) => option.code === currentCode);
    filtered.push({
      code: currentCode,
      description: currentOption?.description ?? currentCode,
      disabled: true,
    });
  }

  return filtered;
};

type EditableDatePickerProps = {
  label: string;
  value: string;
  error?: string;
  onValidChange: (value: dayjs.Dayjs | null) => void;
};

function EditableDatePicker({
  label,
  value,
  error,
  onValidChange,
}: Readonly<EditableDatePickerProps>) {
  const [pickerValue, setPickerValue] = useState(() => (value ? dayjs(value, 'MM/DD/YYYY') : null));
  const [hasInputError, setHasInputError] = useState(false);

  useEffect(() => {
    if (!hasInputError) {
      setPickerValue(value ? dayjs(value, 'MM/DD/YYYY') : null);
    }
  }, [hasInputError, value]);

  return (
    <DatePicker
      label={label}
      format="MM/DD/YYYY"
      value={pickerValue}
      onChange={(nextValue) => {
        setPickerValue(nextValue);
        if (nextValue === null || nextValue.isValid()) {
          setHasInputError(false);
          onValidChange(nextValue);
        }
      }}
      onError={(reason) => {
        setHasInputError(Boolean(reason));
        if (reason) onValidChange(null);
      }}
      slotProps={{
        textField: {
          fullWidth: true,
          size: 'small',
          error: Boolean(error),
          helperText: error,
        },
      }}
    />
  );
}

function FieldSkeletonGrid({ count }: { count: number }) {
  return (
    <Grid container spacing={2} columns={4}>
      {Array.from({ length: count }).map((_, index) => (
        <Grid key={index} size={{ xs: 4, md: 1 }}>
          <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
        </Grid>
      ))}
    </Grid>
  );
}

export default function UnitStatHeaderEdit({
  data,
  draft,
  setDraft,
  lookups,
  errors,
  loading = false,
}: UnitStatHeaderEditProps) {
  const update = <K extends keyof UnitStatHeaderDraft>(field: K, value: UnitStatHeaderDraft[K]) =>
    setDraft((current) => ({ ...current, [field]: value }));
  const textField = (
    field: keyof UnitStatHeaderDraft,
    label: string,
    value: string,
    maxLength?: number
  ) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={value}
      onChange={(event) => update(field, event.target.value as never)}
      error={Boolean(errors[field])}
      helperText={errors[field]}
      slotProps={{ htmlInput: maxLength ? { maxLength } : undefined }}
    />
  );
  const numericField = (
    field: keyof UnitStatHeaderDraft,
    label: string,
    value: string,
    maxLength?: number
  ) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={value}
      onChange={(event) => update(field, event.target.value.replace(/\D/g, '') as never)}
      error={Boolean(errors[field])}
      helperText={errors[field]}
      slotProps={{
        htmlInput: { inputMode: 'numeric', pattern: '[0-9]*', ...(maxLength ? { maxLength } : {}) },
      }}
    />
  );
  const selectField = (
    field: keyof UnitStatHeaderDraft,
    label: string,
    value: string | number,
    options: Array<{ code: string; description: string; disabled?: boolean }>
  ) => (
    <TextField
      select
      fullWidth
      size="small"
      label={label}
      value={value}
      onChange={(event) => update(field, event.target.value as never)}
      error={Boolean(errors[field])}
      helperText={errors[field]}
    >
      {options.map((option) => (
        <MenuItem key={option.code} value={option.code} disabled={option.disabled}>
          {option.description}
        </MenuItem>
      ))}
    </TextField>
  );

  if (loading || !data) {
    return (
      <Box sx={{ pt: 2 }}>
        <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 2 }}>
          Policy info
        </Typography>
        <FieldSkeletonGrid count={POLICY_INFO_FIELD_COUNT} />
        <Typography variant="subtitle1" sx={{ color: 'text.primary', pt: 4, mb: 2 }}>
          Policy type ID
        </Typography>
        <FieldSkeletonGrid count={POLICY_TYPE_FIELD_COUNT} />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 2 }}>
      <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 2 }}>
        Policy info
      </Typography>
      <Grid container spacing={2} columns={4}>
        <Grid size={{ xs: 4, md: 1 }}>
          <Box data-testid="unit-stat-header-insured-name-field">
            {textField('insuredName', "Insured's name", draft.insuredName, 79)}
          </Box>
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          {textField('insuredAddress', 'Address', draft.insuredAddress, 79)}
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <TextField
            fullWidth
            size="small"
            label="Received date"
            value={data.receivedDate ? dayjs(data.receivedDate).format('MM/DD/YYYY') : ''}
            disabled
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <TextField
            fullWidth
            size="small"
            label="Submission ID"
            value={data.submissionId ?? ''}
            disabled
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          {selectField(
            'correctionTypeCode',
            'Correction type',
            draft.correctionTypeCode,
            getAllowedCorrectionTypeOptions(lookups.correctionTypes, draft.correctionTypeCode)
          )}
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Replacement indicator"
            value={draft.replacementReportIndicator ? 'true' : 'false'}
            onChange={(event) =>
              update('replacementReportIndicator', event.target.value === 'true')
            }
          >
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </TextField>
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Exposure state"
            value={draft.exposureStateCode}
            onChange={(event) =>
              update('exposureStateCode', event.target.value ? Number(event.target.value) : '')
            }
          >
            {lookups.states.map((state) => (
              <MenuItem key={state.stateCode} value={state.stateCode}>
                {state.stateAbbreviation} - {state.stateName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <EditableDatePicker
            label="State effective date"
            value={draft.stateEffectiveDate}
            error={errors.stateEffectiveDate}
            onValidChange={(value) =>
              update('stateEffectiveDate', value?.isValid() ? value.format('MM/DD/YYYY') : '')
            }
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          {numericField('riskNumber', 'Risk ID', draft.riskNumber, 9)}
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <TextField
            fullWidth
            size="small"
            label="Accepted date"
            value={data.acceptedDate ? dayjs(data.acceptedDate).format('MM/DD/YYYY') : ''}
            disabled
          />
        </Grid>
      </Grid>

      <Typography variant="subtitle1" sx={{ color: 'text.primary', pt: 4, mb: 2 }}>
        Policy type ID
      </Typography>
      <Grid container spacing={2} columns={4}>
        <Grid size={{ xs: 4, md: 1 }}>
          {selectField(
            'coverageTypeCode',
            'Type coverage',
            draft.coverageTypeCode,
            getAllowedCoverageTypeOptions(lookups.coverageTypes, draft.coverageTypeCode)
          )}
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          {selectField('planTypeCode', 'Market', draft.planTypeCode, MARKET_TYPE_OPTIONS)}
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <TextField
            fullWidth
            size="small"
            label="Non standard indicator"
            value={data.nonStandardCodeNavigation?.nonStandardDescription ?? ''}
            disabled
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          {selectField(
            'estimatedAuditCode',
            'Audit complete',
            draft.estimatedAuditCode,
            AUDIT_COMPLETE_OPTIONS
          )}
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          {selectField(
            'lossesSubjectDeductibleCode',
            'Losses subject to deductible',
            draft.lossesSubjectDeductibleCode,
            lookups.lossesSubjectDeductibleTypes
          )}
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          {numericField(
            'deductibleClaimAccident',
            'Deductible amount - Claim / Accident',
            draft.deductibleClaimAccident
          )}
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          {numericField(
            'deductibleAggregate',
            'Deductible amount - Aggregate',
            draft.deductibleAggregate
          )}
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          {selectField(
            'basisDeductibleCalculationCode',
            'Basis of deductible calculation',
            draft.basisDeductibleCalculationCode,
            getAllowedBasisDeductibleCalculationOptions(
              lookups.basisDeductibleCalculationTypes,
              draft.basisDeductibleCalculationCode
            )
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
